// content/textos.ts - Textos da Dinâmica (entrega 1.6).
//
// Um lugar só para o texto das telas, como no hub (D113). Cada bloco diz de onde veio (`origem`): os do
// `07_VOZ_E_TEXTOS.md` entram literais; os que a Dinâmica nova pediu foram aprovados na P177 (D177) e estão
// no §5g. A marcação é metadado: nunca aparece na tela.

export interface Origem {
  fonte: string;
  /** Só na §5e do 07_VOZ: PLACEHOLDER-MKT, revisão com marketing depois do corte (D146, D149). */
  revisao?: 'marketing';
  /** Texto novo, ainda sem aprovação (número da pergunta no registro). */
  pendente?: string;
}

const VOZ_5G = (itens: string): Origem => ({ fonte: `07_VOZ §5g, ${itens}` });

/** Pedido (07_VOZ §5, "Dinâmica, pedido"; §2, glossário). */
export const pedido = {
  origem: { fonte: '07_VOZ §5 e §2' } satisfies Origem,
  titulo: 'Uma dinâmica para a sua turma',
  subtitulo: 'Escolha a turma e o objetivo. A WiniEduca prepara o resto.',
  sobre: 'Sobre a turma',
  idade: 'Idade da turma',
  materia: 'Matéria',
  objetivo: 'Objetivo da aula',
  onde: 'Onde vai acontecer?',
  quem: 'Quem está na sua turma?',
  dua: 'A dinâmica já vem adaptada. Isso é o DUA: a aula pensada para todos desde o início.',
  algoMais: 'Algo mais? (opcional)',
};

/** Ajuda do campo do objetivo (07_VOZ §5e.8, item 62; PLACEHOLDER-MKT). */
export const ajudaObjetivo = {
  origem: { fonte: '07_VOZ §5e.8, item 62', revisao: 'marketing' } satisfies Origem,
  texto: 'Escolha o objetivo',
};

/** O que a caixa mostra antes da escolha (07_VOZ §5f, item 46). */
export const semEscolha = {
  origem: { fonte: '07_VOZ §5f, item 46' } satisfies Origem,
  materia: 'Escolha a matéria',
};

export const pedidoNovos = {
  origem: VOZ_5G('itens 1 a 5'),
  anos: (idade: number) => `${idade} anos`,
  ondePlaceholder: 'Escolha o lugar',
  algoMaisExemplo: 'Por exemplo: usar o tema dos dinossauros',
  contador: (usados: number, limite: number) => `${usados} de ${limite} caracteres`,
  erros: {
    materia: 'Escolha a matéria.',
    objetivo: 'Escolha o objetivo da aula.',
    onde: 'Escolha onde vai acontecer.',
    algoMais: 'Use até 200 caracteres.',
  },
};

/** Rótulos de ação (07_VOZ §4). */
export const acoes = {
  origem: { fonte: '07_VOZ §4' } satisfies Origem,
  criar: 'Criar dinâmica',
  pdf: 'Salvar em PDF',
  whatsapp: 'WhatsApp',
  outra: 'Criar outra',
  entrar: 'Entrar',
};

/** Computador: "O que você vai receber" (07_VOZ §5, "Dinâmica, computador"; D83). */
export const recebe = {
  origem: { fonte: '07_VOZ §5' } satisfies Origem,
  titulo: 'O que você vai receber',
  passo: 'Passo a passo',
  dicas: 'Dicas para todos os alunos',
  porque: 'Por que funciona',
  pdf: 'PDF para imprimir',
};

export const recebeApoio = {
  origem: VOZ_5G('item 6'),
  passo: 'Para aplicar sem precisar adaptar.',
  dicas: 'Adaptações para quem você marcou (DUA).',
  porque: 'O JiTT, que ajusta a aula ao que a turma já sabe.',
  pdf: 'Pronto em cerca de 1 minuto.',
};

/** Espera (07_VOZ §5, "Dinâmica, espera"; D78, D80). */
export const espera = {
  origem: { fonte: '07_VOZ §5' } satisfies Origem,
  titulo: 'Preparando a sua dinâmica',
  tempo: 'Leva cerca de 1 minuto.',
  etapas: ['Entendendo a sua turma', 'Montando o passo a passo', 'Adaptando para todos os alunos', 'Explicando por que funciona'],
  fique: 'Fique nesta tela até a dinâmica ficar pronta. A tela do celular fica acesa enquanto isso.',
  sair: 'Se sair agora, a dinâmica não fica pronta. Sair mesmo assim?',
};

export const esperaNovos = {
  origem: VOZ_5G('itens 7 a 9'),
  faltam: (segundos: number) => `Faltam uns ${segundos} segundos`,
  quase: 'Quase pronta.',
  demorando: 'Está levando mais tempo que o normal. Continue nesta tela.',
  enquantoIsso: 'Enquanto isso',
  dica: 'O JiTT ajusta a aula ao que a turma já sabe. Antes de aplicar a dinâmica, pergunte aos alunos o que eles já conhecem sobre o tema.',
  confirmarSair: 'Sair',
  ficar: 'Ficar nesta tela',
};

/** Pronta (07_VOZ §5, "Dinâmica, pronta"). */
export const pronta = {
  origem: { fonte: '07_VOZ §5' } satisfies Origem,
  selo: 'Pronta para aplicar',
  passo: 'Passo a passo',
  dua: 'Para todos os alunos (DUA)',
};

export const prontaNovos = {
  origem: VOZ_5G('itens 10 a 16'),
  objetivos: 'Objetivos',
  materiais: 'Materiais para a aula',
  baixar: 'Baixar',
  baixarFalha: 'Não conseguimos baixar a imagem agora. Tente de novo.',
  ouvir: 'Ouvir o passo a passo',
  pausar: 'Pausar',
  ouvirDoComeco: 'Ouvir do começo',
  audioFalha: 'Não conseguimos tocar o áudio agora. Tente de novo.',
  pdfFalha: 'Não conseguimos preparar o PDF agora. Tente de novo.',
  ia: 'A dinâmica é criada com IA. Leia antes de aplicar e, se algo estiver errado, use “Reportar um problema”.',
  criadaEm: (data: string) => `Criada na WiniEduca em ${data}.`,
};

/** Mensagem do WhatsApp (P32 em D41; 07_VOZ §5g, item 17). */
export const whatsapp = {
  origem: VOZ_5G('item 17'),
  mensagem: (titulo: string, tempo: string, passos: string[], link: string) =>
    [`*${titulo}*`, tempo, '', 'Passo a passo:', ...passos.map((p, i) => `${i + 1}. ${p}`), '', `Criada na WiniEduca: ${link}`].join('\n'),
};

/** Pergunta de feedback depois de cada dinâmica (07_VOZ §5b; D104). */
export const feedback = {
  origem: { fonte: '07_VOZ §5b' } satisfies Origem,
  pergunta: 'Esta dinâmica ajudou?',
  sim: 'Sim',
  nao: 'Não',
  contar: 'Quer contar mais? (opcional)',
};

export const feedbackNovos = {
  origem: VOZ_5G('item 18'),
  enviar: 'Enviar',
  obrigado: 'Obrigado pela resposta.',
};

/** Campo do "Quer contar mais?" vazio (07_VOZ §5f, item 44). */
export const comentarioVazio = {
  origem: { fonte: '07_VOZ §5f, item 44' } satisfies Origem,
  texto: 'Escreva a sua sugestão.',
};

/** Reportar um problema (07_VOZ §5g, itens 19 a 21). */
export const reportar = {
  origem: VOZ_5G('itens 19 a 21'),
  abrir: 'Reportar um problema',
  titulo: 'O que deu errado?',
  campo: 'Conte o que aconteceu',
  ajuda: 'Isso nos ajuda a corrigir a Dinâmica.',
  vazio: 'Conte o que aconteceu.',
  enviar: 'Enviar',
  cancelar: 'Cancelar',
  enviado: 'Recebemos o seu relato. Obrigado!',
  falha: 'Não conseguimos enviar agora. Tente de novo em alguns minutos.',
};

/** Limites (07_VOZ §5c; D109). */
export const limites = {
  origem: { fonte: '07_VOZ §5c' } satisfies Origem,
  restam: (n: number) => `Você ainda pode criar ${n} dinâmicas hoje.`,
  cota: 'Você usou as dinâmicas de hoje. Amanhã, à meia-noite, elas voltam.',
  pausa: 'Por segurança, pausamos novas dinâmicas até amanhã. Suas dinâmicas continuam salvas.',
};

/** Erros da criação e das opções (07_VOZ §5g, itens 22 a 24; "Sem internet" é o do §5f, item 7). */
export const erros = {
  origem: VOZ_5G('itens 22 a 24'),
  criar: 'Não conseguimos criar a dinâmica agora. Tente de novo em alguns minutos.',
  campos: 'Confira os campos e tente de novo.',
  opcoes: 'Não conseguimos carregar as opções agora. Tente de novo em alguns minutos.',
  tentar: 'Tentar de novo',
};

export const semInternet = {
  origem: { fonte: '07_VOZ §5f, item 7' } satisfies Origem,
  texto: 'Sem internet. Confira a conexão e tente de novo.',
};

/** Suas dinâmicas (o histórico; 07_VOZ §5g, itens 25 a 27). */
export const historico = {
  origem: VOZ_5G('itens 25 a 27'),
  titulo: 'Suas dinâmicas',
  abrir: 'Abrir',
  remover: 'Remover',
  confirmar: 'Remover esta dinâmica? Ela sai da sua lista.',
  cancelar: 'Cancelar',
  vazio: 'Suas dinâmicas vão aparecer aqui.',
  falha: 'Não conseguimos carregar as suas dinâmicas agora.',
  removerFalha: 'Não conseguimos remover agora. Tente de novo.',
};

/** Entrada pelo hub (login único; 07_VOZ §5g, itens 28 e 29). */
export const entrada = {
  origem: VOZ_5G('item 28'),
  titulo: 'Entre na sua conta para criar dinâmicas',
  texto: 'A Dinâmica Pedagógica faz parte da WiniEduca. Entre com a sua conta e volte para cá.',
  tentar: 'Tentar de novo',
};

/** Caminho e aba (padrão da conta, 07_VOZ §5f, item 43). */
export const pagina = {
  origem: VOZ_5G('item 30'),
  aba: 'Criar dinâmica | WiniEduca',
  caminho: 'Criar dinâmica',
};
